import { InputHTMLAttributes, ReactNode, forwardRef } from 'react';

/**
 * Input — matches DreamsPOS form-control pattern. Supports leading/trailing
 * icons, label, helper, error.
 */
interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helper?: string;
  error?: string;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
}

export default forwardRef<HTMLInputElement, Props>(function Input(
  { label, helper, error, leadingIcon, trailingIcon, fullWidth = true, className = '', id, ...rest },
  ref,
) {
  const inputId = id || rest.name;
  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label htmlFor={inputId} className="block text-13 font-medium text-heading mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        {leadingIcon && (
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            {leadingIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          className={[
            'w-full h-10 rounded-md border bg-surface text-13 text-heading',
            'outline-none transition-colors placeholder:text-gray-400',
            error
              ? 'border-danger focus:border-danger'
              : 'border-border focus:border-primary',
            leadingIcon ? 'pl-10' : 'pl-3.5',
            trailingIcon ? 'pr-10' : 'pr-3.5',
            'disabled:bg-light disabled:cursor-not-allowed',
            className,
          ].join(' ')}
          {...rest}
        />
        {trailingIcon && (
          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400">
            {trailingIcon}
          </span>
        )}
      </div>
      {(helper || error) && (
        <p className={['text-11 mt-1', error ? 'text-danger' : 'text-gray-500'].join(' ')}>
          {error || helper}
        </p>
      )}
    </div>
  );
});
